import React, { useState, useEffect, useRef, FC } from 'react';
import { usePublicPropertyDetails } from '../hooks/usePublicPropertyDetails';
import PublicPropertyPopupBody from './PublicPropertyPopupBody';
import PublicPropertyPopupFooter from './PublicPropertyPopupFooter';
import placeholder from 'images/placeholder.webp';
import { CloseIconDualBorder } from '../../../../components/icons/OurIcons';

export type PublicPropertyPopupProps = {
  propertyId: string;
  setPopupLoading?: (loading: boolean) => void;
  setHeight?: (height: number) => void;
  closePopup: () => void;
};

const PublicPropertyPopup: FC<PublicPropertyPopupProps> = ({
  propertyId,
  setPopupLoading = () => null,
  setHeight = () => null,
  closePopup = () => null,
}) => {
  const [ propertyImages, setPropertyImages ] = useState([]);
  const [ imageLoaded, setImageLoaded ] = useState(false);
  const { propertyDetails, isLoading } = usePublicPropertyDetails(propertyId);
  const popupRef = useRef(null);

  useEffect(() => {
    if (propertyDetails && propertyDetails.imgUrl) {
      // Backend returns single imgUrl, wrap it in array format for carousel
      setPropertyImages([ {
        image_url: propertyDetails.imgUrl,
        id: '1',
        preferred_order: 0
      } ]);
    } else if (propertyDetails) {
      // No image, use placeholder
      setPropertyImages([ { image_url: placeholder, id: '1', preferred_order: 0 } ]);
    }
  }, [ propertyDetails ]);

  useEffect(() => {
    setPopupLoading(isLoading);
  }, [ isLoading, setPopupLoading ]);

  useEffect(() => {
    if (popupRef.current) {
      setHeight(popupRef.current.offsetHeight);
    }
    // Re-measure once details render too: the popup stays hidden until it has a height, so waiting on the
    // image alone could leave it invisible (e.g. no photo, or the details request failing).
  }, [ imageLoaded, setHeight, propertyDetails, isLoading ]);

  const closePopupClickHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    closePopup();
  };

  if (!propertyDetails && !isLoading) {
    return (
      <div ref={popupRef} className="p-8 bg-slate-300 z-40">
        No property details
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-8 bg-white z-40">
        Loading...
      </div>
    );
  }

  // Transform backend camelCase flat response to snake_case nested format expected by Footer
  // Backend returns: { avm, equity, avmPerSqft, beds, baths, listPrice, etc. }
  // Footer expects: { list_price, bedrooms, bathrooms, address: { street, city }, etc. }
  const propertyForFooter = {
    id: propertyDetails.id,
    list_price: propertyDetails.listPrice,
    bedrooms: propertyDetails.beds,
    bathrooms: propertyDetails.baths,
    sqft: propertyDetails.sqft,
    status: propertyDetails.status,
    address: {
      // Backend returns flat address string, parse it
      street: propertyDetails.address?.split(',')[0]?.trim() || '',
      city: propertyDetails.address?.split(',')[1]?.trim() || '',
    },
    year_built: propertyDetails.yearBuilt,
    garage_spaces: propertyDetails.garage,
    total_tax_amount: propertyDetails.annualTaxes,
    assessed_value: propertyDetails.taxAssessedValue,
    subdivision: propertyDetails.subdivision,
  };

  // Transform AVM data from backend camelCase to snake_case
  const avmResult = propertyDetails.avm
    ? {
      id: null, // Backend doesn't return AVM ID in this endpoint
      estimated_value: propertyDetails.avm,
      estimated_dollar_per_sqft: propertyDetails.avmPerSqft,
      potential_equity: propertyDetails.equity,
    }
    : null;

  return (
    <div
      id="property-detail-popup-container"
      className="cursor-pointer rounded-b-lg"
      ref={popupRef}
      onClick={() => window.open(`/properties/${propertyId}`, '_blank')}
    >
      <div className="absolute z-10 top-0 left-0" onClick={e => closePopupClickHandler(e)}>
        <CloseIconDualBorder size={24} />
      </div>
      <PublicPropertyPopupBody
        propertyImages={propertyImages}
        propertyId={propertyDetails.id}
        setImageLoaded={setImageLoaded}
      />
      <PublicPropertyPopupFooter
        property={propertyForFooter as any}
        avmResult={avmResult as any}
        isAvmComp={false}
      />
    </div>
  );
};

export default PublicPropertyPopup;
